import React, { useEffect } from 'react';

const AccessibilityEnhancer: React.FC = () => {
  useEffect(() => {
    // Enhance accessibility for all buttons
    const enhanceButtons = () => {
      const buttons = document.querySelectorAll('button:not([aria-label]):not([aria-labelledby])');
      buttons.forEach((button, index) => {
        if (!button.getAttribute('aria-label')) {
          const text = button.textContent?.trim() || `Button ${index + 1}`;
          button.setAttribute('aria-label', text);
        }
      });
    };

    // Enhance accessibility for images
    const enhanceImages = () => {
      const images = document.querySelectorAll('img:not([alt])');
      images.forEach((img) => {
        if (!img.getAttribute('alt')) {
          img.setAttribute('alt', 'MotivaMatch career development image');
        }
      });
    };

    // Enhance accessibility for links
    const enhanceLinks = () => {
      const links = document.querySelectorAll('a:not([aria-label])');
      links.forEach((link) => {
        if (!link.getAttribute('aria-label') && !link.textContent?.trim()) {
          const href = link.getAttribute('href') || '';
          link.setAttribute('aria-label', `Navigate to ${href}`);
        }
      });
    };

    // Enhance heading hierarchy
    const enhanceHeadings = () => {
      const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
      let currentLevel = 0;
      
      headings.forEach((heading) => {
        const level = parseInt(heading.tagName.charAt(1));
        if (level > currentLevel + 1) {
          console.warn(`Heading hierarchy issue: ${heading.tagName} after h${currentLevel}`);
        }
        currentLevel = level;
      });
    };

    // Add skip navigation link
    const addSkipNavigation = () => {
      const skipLink = document.createElement('a');
      skipLink.href = '#main-content';
      skipLink.textContent = 'Skip to main content';
      skipLink.style.cssText = `
        position: absolute;
        top: -40px;
        left: 6px;
        background: #000;
        color: #fff;
        padding: 8px;
        text-decoration: none;
        z-index: 1000;
        transition: top 0.3s;
      `;
      skipLink.addEventListener('focus', () => {
        skipLink.style.top = '6px';
      });
      skipLink.addEventListener('blur', () => {
        skipLink.style.top = '-40px';
      });
      document.body.insertBefore(skipLink, document.body.firstChild);
    };

    // Add main content landmark
    const addMainLandmark = () => {
      const mainContent = document.querySelector('main') || document.querySelector('#root');
      if (mainContent && !mainContent.getAttribute('role')) {
        mainContent.setAttribute('role', 'main');
        mainContent.setAttribute('id', 'main-content');
      }
    };

    // Enhance form accessibility
    const enhanceForms = () => {
      const inputs = document.querySelectorAll('input:not([aria-label]):not([aria-labelledby])');
      inputs.forEach((input) => {
        const label = document.querySelector(`label[for="${input.id}"]`);
        if (!label && !input.getAttribute('aria-label')) {
          const placeholder = input.getAttribute('placeholder') || 'Input field';
          input.setAttribute('aria-label', placeholder);
        }
      });
    };

    // Add high contrast mode support
    const addHighContrastSupport = () => {
      const style = document.createElement('style');
      style.textContent = `
        @media (prefers-contrast: high) {
          * {
            border-color: currentColor !important;
          }
          button, a {
            border: 2px solid currentColor !important;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `;
      document.head.appendChild(style);
    };

    // Initialize all enhancements
    const initializeAccessibility = () => {
      enhanceButtons();
      enhanceImages();
      enhanceLinks();
      enhanceHeadings();
      addSkipNavigation();
      addMainLandmark();
      enhanceForms();
      addHighContrastSupport();
    };

    // Run on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeAccessibility);
    } else {
      initializeAccessibility();
    }

    // Re-run on dynamic content changes
    const setupObserver = () => {
      try {
        if (!document.body || !(document.body instanceof Node)) {
          setTimeout(setupObserver, 100);
          return () => {};
        }
        
        const observer = new MutationObserver(() => {
          enhanceButtons();
          enhanceImages();
          enhanceLinks();
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true
        });

        return () => observer.disconnect();
      } catch (error) {
        console.debug('Accessibility observer setup failed:', error);
        return () => {}; // Return empty cleanup function
      }
    };
    
    let cleanup: (() => void) | undefined;
    
    // Wait for body to be ready
    if (document.body) {
      cleanup = setupObserver();
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        cleanup = setupObserver();
      });
      cleanup = () => {}; // Temporary cleanup until observer is set up
    }

    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  return null;
};

export default AccessibilityEnhancer;
